import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { 
  CalendarDays, ChevronLeft, ChevronRight, Loader2, Flame, CheckCircle2, X
} from 'lucide-react';
import { toLocalDateStr } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChoreCalendar = ({ onClose }) => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState({});
  const [streak, setStreak] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/chores/history?days=90`, { 
        headers: getAuthHeader() 
      });
      setHistory(res.data.history || {});
      setStreak(res.data.streak || 0);
    } catch (error) {
      console.error('Failed to fetch chore history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  // Get completion status for a date
  const getDateStatus = (date) => {
    const dateStr = toLocalDateStr(date);
    const record = history[dateStr];
    if (!record) return null;
    
    const rate = record.completion_rate;
    if (rate >= 100) return 'complete';
    if (rate >= 80) return 'good';
    if (rate >= 50) return 'partial';
    if (rate > 0) return 'started';
    return 'missed';
  };

  // Get the selected date's details
  const selectedDateStr = selectedDate ? toLocalDateStr(selectedDate) : undefined;
  const selectedRecord = history[selectedDateStr];

  // Custom day rendering for calendar
  const modifiers = {
    complete: [],
    good: [],
    partial: [],
    started: [],
    missed: []
  };

  Object.entries(history).forEach(([dateStr, record]) => {
    const date = new Date(dateStr + 'T12:00:00');
    const rate = record.completion_rate;
    
    if (rate >= 100) modifiers.complete.push(date);
    else if (rate >= 80) modifiers.good.push(date);
    else if (rate >= 50) modifiers.partial.push(date);
    else if (rate > 0) modifiers.started.push(date);
    else modifiers.missed.push(date);
  });

  const modifiersStyles = {
    complete: { 
      backgroundColor: 'rgb(34 197 94)', 
      color: 'white',
      borderRadius: '50%'
    },
    good: { 
      backgroundColor: 'rgb(132 204 22)', 
      color: 'white',
      borderRadius: '50%'
    },
    partial: { 
      backgroundColor: 'rgb(250 204 21)', 
      color: 'black',
      borderRadius: '50%'
    },
    started: { 
      backgroundColor: 'rgb(251 146 60)', 
      color: 'white',
      borderRadius: '50%'
    },
    missed: { 
      backgroundColor: 'rgb(239 68 68)', 
      color: 'white',
      borderRadius: '50%'
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="chore-calendar">
      {/* Header with streak */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-medium">Chore History</h3>
        </div>
        <div className="flex items-center gap-4">
          {streak > 0 && (
            <div className="flex items-center gap-1 text-orange-500" title="Current streak">
              <Flame className="w-5 h-5" />
              <span className="font-bold">{streak} day{streak !== 1 ? 's' : ''}</span>
            </div>
          )}
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Calendar */}
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          modifiers={modifiers}
          modifiersStyles={modifiersStyles}
          className="rounded-md border"
          disabled={(date) => date > new Date()}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span>100%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-lime-500"></div>
          <span>80%+</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <span>50%+</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-orange-400"></div>
          <span>&lt;50%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span>0%</span>
        </div>
      </div>

      {/* Selected date details */}
      {selectedRecord && (
        <Card className="bg-muted/30">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {new Date(selectedDateStr + 'T12:00:00').toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedRecord.completed_count} of {selectedRecord.total} chores completed
                </p>
              </div>
              <div className={`text-2xl font-bold ${
                selectedRecord.completion_rate >= 100 ? 'text-green-600' :
                selectedRecord.completion_rate >= 80 ? 'text-lime-600' :
                selectedRecord.completion_rate >= 50 ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {selectedRecord.completion_rate}%
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-700">
            {Object.values(history).filter(h => h.completion_rate >= 100).length}
          </p>
          <p className="text-xs text-green-600">Perfect Days</p>
        </div>
        <div className="p-3 bg-lime-50 rounded-lg">
          <p className="text-2xl font-bold text-lime-700">
            {Object.values(history).filter(h => h.completion_rate >= 80 && h.completion_rate < 100).length}
          </p>
          <p className="text-xs text-lime-600">Good Days</p>
        </div>
        <div className="p-3 bg-amber-50 rounded-lg">
          <p className="text-2xl font-bold text-amber-700">{streak}</p>
          <p className="text-xs text-amber-600">Day Streak</p>
        </div>
      </div>
    </div>
  );
};

export default ChoreCalendar;
